import React, { useState, useEffect } from 'react';
import { dataApi } from '../services/api';

// shadcn UI components
import { Skeleton } from './ui/skeleton';

const PreviewImageThumbnails = ({ quoteRequestId, quoteId, imageMetadata = [], maxImages = 4 }) => {
  const [loadedImages, setLoadedImages] = useState({});
  const [imageRecords, setImageRecords] = useState(imageMetadata || []);
  const [loading, setLoading] = useState(false);

  const targetQuoteRequestId = quoteRequestId || quoteId;

  useEffect(() => {
    if (imageMetadata && imageMetadata.length > 0) {
      setImageRecords(imageMetadata);
    }
  }, [imageMetadata]);

  useEffect(() => {
    if (!targetQuoteRequestId) {
      return;
    }

    let isMounted = true;

    const loadPreviewImages = async () => {
      try {
        setLoading(true);

        const images = await dataApi.quoteRequestImages(targetQuoteRequestId);

        if (!isMounted || !images) {
          return;
        }

        const processedImages = {};
        const imagesToShow = images.slice(0, maxImages);

        imagesToShow.forEach((imageData) => {
          if (imageData.url) {
            processedImages[imageData.id] = {
              ...imageData,
              src: imageData.url,
              loaded: true,
              type: 'url'
            };
          }
        });

        setLoadedImages(processedImages);
        setImageRecords(images);
      } catch (err) {
        console.error('Error loading preview images:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadPreviewImages();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetQuoteRequestId, maxImages]);

  if (!targetQuoteRequestId && imageRecords.length === 0) {
    return null;
  }

  const displayImages = imageRecords.slice(0, maxImages);
  const remainingCount = Math.max(0, imageRecords.length - maxImages);

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Images</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {displayImages.map((imageMeta, index) => {
          const loadedImage = loadedImages[imageMeta.id];

          return (
            <div key={imageMeta.id || index} className="relative aspect-square bg-muted rounded-lg overflow-hidden border">
              {loading && !loadedImage && (
                <Skeleton className="w-full h-full" />
              )}

              {loadedImage && (
                <div className="relative w-full h-full">
                  <img
                    src={loadedImage.src}
                    alt={loadedImage.image_name || `Image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              {!loading && !loadedImage && (
                <Skeleton className="w-full h-full" />
              )}
            </div>
          );
        })}

        {remainingCount > 0 && (
          <div className="relative aspect-square bg-muted/50 rounded-lg overflow-hidden border flex items-center justify-center text-sm font-medium text-muted-foreground">
            +{remainingCount} more image{remainingCount !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {!loading && displayImages.length === 0 && (
        <p className="text-sm text-muted-foreground">No images uploaded yet.</p>
      )}
    </div>
  );
};

export default PreviewImageThumbnails;